import { UserRole } from '@/types'
import { createTheme, alpha, lighten, darken } from '@mui/material/styles'

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary']
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary']
  }

  interface PaletteColor {
    lighter?: string
  }

  interface SimplePaletteColorOptions {
    lighter?: string
  }
}

/**
 * Role-based color configuration
 */
const getRoleColors = (role: UserRole | null) => {
  if (!role) {
    return {
      primary: '#3BAE78', // Default green for no role
      secondary: '#6BC799',
    }
  }

  switch (role) {
    case UserRole.THERAPIST:
      return {
        primary: '#2E7DAF', // Blue for providers
        secondary: '#5B9BD5', // Lighter blue for secondary
      }
    case UserRole.CLIENT:
      return {
        primary: '#3BAE78', // Green for clients
        secondary: '#6BC799', // Lighter green for secondary
      }
    case UserRole.ADMIN:
      return {
        primary: '#5BC0BE', // Green for admin (can be changed later)
        secondary: '#6BC799', // Lighter green for secondary
      }
    case UserRole.CERTIFICATE_MANAGER:
      return {
        primary: '#5BC0BE', // Same as admin for certificate manager
        secondary: '#6BC799', // Same as admin for secondary
      }
    case UserRole.AGENCY:
      return {
        primary: '#7A6FA1', // Orange for agencies
        secondary: '#c6b7efff', // Lighter orange for secondary
      }
    default:
      return {
        primary: '#3BAE78',
        secondary: '#6BC799',
      }
  }
}

export const theme = (role: UserRole | null) => {
  const roleColors = getRoleColors(role)

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: roleColors.primary,
        light: lighten(roleColors.primary, 0.3),
        dark: darken(roleColors.primary, 0.2),
        contrastText: '#ffffff',
      },
      secondary: {
        main: roleColors.secondary,
        light: lighten(roleColors.secondary, 0.2),
        dark: darken(roleColors.secondary, 0.2),
        contrastText: '#ffffff',
      },
      tertiary: {
        main: '#0ea5e9',
        light: '#38bdf8',
        dark: '#0284c7',
        contrastText: '#ffffff',
      },
      background: {
        default: '#fafafa',
        paper: '#ffffff',
      },
      text: {
        primary: '#1f2937',
        secondary: '#6b7280',
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626',
        lighter: '#fee2e2',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
        lighter: '#fef3c7',
      },
      success: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669',
        lighter: '#d1fae5',
      },
      info: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#2563eb',
        lighter: '#dbeafe',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            padding: '8px 16px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
          },
          // containedPrimary: {
          //   background: 'linear-gradient(45deg, #0ea5e9 30%, #38bdf8 90%)',
          //   '&:hover': {
          //     background: 'linear-gradient(45deg, #0284c7 30%, #0ea5e9 90%)',
          //   },
          // },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  })
}
